import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { Notification } from '../types/notification';
import { fetchNotifications, fetchUnreadCount, markAsRead } from '../utils/notification-service';

// Extended type for session context including properties we know exist
interface ExtendedSessionContextType {
  doesSessionExist: boolean;
  userId?: string;
  loading: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  refreshNotifications: () => Promise<void>;
  markNotificationAsRead: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  // Cast to our extended type
  const session = useSessionContext() as ExtendedSessionContextType;
  
  // Use refs to prevent infinite loops
  const lastRefreshTimeRef = useRef<number>(0);
  const isPollingRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Define refreshNotifications function
  const refreshNotifications = async () => {
    // Throttle API calls - only allow refresh every 5 seconds
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < 5000) {
      console.log('Skipping notification refresh - throttled');
      return;
    }

    if (!session.doesSessionExist) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    lastRefreshTimeRef.current = now;
    setLoading(true);
    
    try {
      const [notificationsData, count] = await Promise.all([
        fetchNotifications(),
        fetchUnreadCount()
      ]);
      
      setNotifications(notificationsData);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to refresh notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markNotificationAsRead = async (id: string) => {
    const success = await markAsRead(id);
    if (success) {
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === id ? { ...notif, read: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  // Initial load and refresh when session changes
  useEffect(() => {
    // Only refresh if session exists and has loaded
    if (!session.loading && session.doesSessionExist) {
      refreshNotifications();
    } else if (!session.loading) {
      // Reset notifications when logged out
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
    }
  }, [session.loading, session.doesSessionExist]);

  // Set up periodic refresh (every 30 seconds)
  useEffect(() => {
    if (!session.doesSessionExist || isPollingRef.current) return;
    
    console.log('Setting up notification polling interval');
    isPollingRef.current = true;
    
    const intervalId = setInterval(() => {
      refreshNotifications();
    }, 30000); // 30 seconds
    
    pollingIntervalRef.current = intervalId;
    
    return () => {
      console.log('Clearing notification polling interval');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, [session.doesSessionExist]);

  const value = {
    notifications,
    unreadCount,
    loading,
    refreshNotifications,
    markNotificationAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 